import { useEffect, useRef, useState } from "react";
import { Chip, Menu, MenuItem, ListItemIcon, ListItemText, Avatar } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckIcon from "@mui/icons-material/Check";
import { useGetList, useGetOne, useGetIdentity } from "react-admin";
import { ResourceName, UserStatus, User } from "@/types";
import { getAssigneeName, getInitials } from "../../utils/taskUtils";
import { getFilledChipStyles, getEmptyChipStyles } from "./taskChipStyles";
import { stringToColor } from "@/components/common/fields/AvatarField";
import { ChipSize, UIComponentSize } from "@/types/shared/uiComponents";

interface AssigneeSelectorProps {
  /** Currently selected user ID */
  value: string | null;
  /** Callback when selection changes */
  onChange: (userId: string | null) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Chip size variant */
  size?: ChipSize;
  /**
   * Controlled-open mirror. When the parent sets this to `false` the menu is
   * force-closed, so the composer can dismiss it before the chip row unmounts
   * and avoid a leaked MUI scroll-lock that froze the drawer (#5158 / #6310 AC4).
   */
  open?: boolean;
  /** Notifies the parent whenever the menu opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

export function AssigneeSelector({
  value,
  onChange,
  disabled = false,
  size = UIComponentSize.Medium,
  open,
  onOpenChange,
}: AssigneeSelectorProps) {
  // Stable anchor: the chip element, not the click event target.
  const chipRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: currentUser } = useGetIdentity();

  // Parent force-close: dismiss the menu when controlled `open` flips false so it
  // never unmounts while open.
  useEffect(() => {
    if (open === false) setMenuOpen(false);
  }, [open]);

  // Fetch all active users for the menu
  const { data: users = [], isLoading: isLoadingUsers } = useGetList<User>(ResourceName.USERS, {
    pagination: { page: 1, perPage: 100 },
    filter: { status: UserStatus.ACTIVE },
  });

  // Fetch the selected user's details — retry: false prevents a 404 (cross-tenant unprovisioned user) from
  // triggering react-query's default 3× retry cycle, which causes repeated re-renders that lock the drawer.
  const { data: selectedUser, isError: selectedUserError } = useGetOne<User>(
    ResourceName.USERS,
    { id: value || "" },
    { enabled: !!value, retry: false }
  );

  const handleClick = () => {
    if (!disabled) {
      setMenuOpen(true);
      onOpenChange?.(true);
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
    onOpenChange?.(false);
  };

  const handleSelect = (userId: string | null) => {
    onChange(userId);
    closeMenu();
  };

  const currentUserId = currentUser?.id ? String(currentUser.id) : undefined;
  const displayName = selectedUser ? getAssigneeName(selectedUser, currentUserId) : null;
  const selectedUserColors = displayName ? stringToColor(displayName) : null;

  const filledStyles = getFilledChipStyles({ size, clickable: !disabled });
  const emptyStyles = getEmptyChipStyles({ size, clickable: !disabled });

  return (
    <>
      {value && selectedUser && !selectedUserError && selectedUserColors ? (
        <Chip
          ref={chipRef}
          avatar={
            <Avatar
              sx={{
                width: 20,
                height: 20,
                fontSize: 10,
                bgcolor: selectedUserColors.bgColor,
                color: selectedUserColors.textColor,
              }}
            >
              {getInitials(displayName ?? undefined)}
            </Avatar>
          }
          label={displayName}
          onClick={handleClick}
          size="small"
          sx={filledStyles}
        />
      ) : (
        <Chip
          ref={chipRef}
          icon={<PersonOutlineIcon />}
          label="Assignee"
          onClick={handleClick}
          size="small"
          sx={emptyStyles}
        />
      )}

      <Menu
        anchorEl={chipRef.current}
        open={menuOpen}
        onClose={closeMenu}
        disableScrollLock
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 220, maxHeight: 320 },
          },
        }}
      >
        {isLoadingUsers && (
          <MenuItem disabled>
            <ListItemText>Loading…</ListItemText>
          </MenuItem>
        )}

        {!isLoadingUsers && users.length === 0 && (
          <MenuItem disabled>
            <ListItemText>No users available</ListItemText>
          </MenuItem>
        )}

        {users.map((user) => {
          const isSelected = value === user.id;
          const userName = getAssigneeName(user, currentUserId);
          const { bgColor, textColor } = stringToColor(userName);

          return (
            <MenuItem key={user.id} selected={isSelected} onClick={() => handleSelect(user.id)}>
              <ListItemIcon>
                {isSelected ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: 11,
                      bgcolor: bgColor,
                      color: textColor,
                    }}
                  >
                    {getInitials(userName)}
                  </Avatar>
                )}
              </ListItemIcon>
              <ListItemText>{userName}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
